import { Controller } from "@hotwired/stimulus";
import { getBrowserTimezoneOffset } from "~/lib/date";

// Rewrites date filter preset links to use the browser's local dates and
// timezone offset, so "Today" and "Yesterday" reflect the user's timezone
// rather than the server's (UTC).
//
// Connects with data-controller="timezone-links"
export default class extends Controller<HTMLElement> {
  static targets = ["link"];

  declare linkTargets: HTMLAnchorElement[];

  linkTargetConnected(link: HTMLAnchorElement) {
    const url = new URL(link.href);
    const preset = link.dataset.preset;
    const tz = getBrowserTimezoneOffset();

    const dates = this.datesForPreset(preset, new Date());
    if (dates) {
      url.searchParams.set("created_from", dates.from);
      url.searchParams.set("created_to", dates.to);
    }

    url.searchParams.set("tz", tz);
    link.href = url.toString();
  }

  private datesForPreset(
    preset: string | undefined,
    today: Date,
  ): { from: string; to: string } | null {
    switch (preset) {
      case "today":
        return { from: this.formatDate(today), to: this.formatDate(today) };
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return { from: this.formatDate(yesterday), to: this.formatDate(yesterday) };
      }
      case "this-week": {
        const start = this.startOfWeek(today);
        return { from: this.formatDate(start), to: this.formatDate(today) };
      }
      case "last-week": {
        const lastWeekDay = new Date(today);
        lastWeekDay.setDate(today.getDate() - 7);
        const start = this.startOfWeek(lastWeekDay);
        const end = this.endOfWeek(lastWeekDay);
        return { from: this.formatDate(start), to: this.formatDate(end) };
      }
      case "this-month": {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: this.formatDate(start), to: this.formatDate(today) };
      }
      case "last-month": {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { from: this.formatDate(start), to: this.formatDate(end) };
      }
      default:
        return null;
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Monday-based week start (matching the server's beginning_of_week)
  private startOfWeek(date: Date): Date {
    const result = new Date(date);
    const day = result.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    result.setDate(result.getDate() + diff);
    return result;
  }

  private endOfWeek(date: Date): Date {
    const start = this.startOfWeek(date);
    start.setDate(start.getDate() + 6);
    return start;
  }
}
